import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  useEffect,
} from "react";
import apiClient from "../services/apiClient";
import { v4 as uuidv4 } from "uuid";
import ActiveUpload from "../models/ActiveUpload";
import { UPLOAD_CONTAINER_NAME, UPLOAD_STATUSES } from "../utils/constants";
import { showSuccessNotification } from "../utils/notifications";
import { AbortController as AzureAbortController } from "@azure/abort-controller";
import { BlobServiceClient } from "@azure/storage-blob";
import { sanitizeFilename } from "../services/UploadService";

import UploadsModal from "../components/UploadsModal";
import { UploadsContext } from "./UploadsContext";
import useWarnOnUnload from "../hooks/useWarnOnUnload";
import { useDocumentVisibility } from "@mantine/hooks";

const MAX_CONCURRENT_UPLOADS = 3;

export const UploadContext = createContext({
  uploads: [],
  startUpload: () => {},
  cancelUpload: () => {},
  setUploadsModalOpen: () => {},
  uploadingCount: 0,
});
export function UploadProvider({ children }) {
  const documentState = useDocumentVisibility();
  const isDocumentVisible = documentState === "visible";
  const [uploads, setUploads] = useState([]);
  const { activeUploads } = useContext(UploadsContext) || { activeUploads: [] };

  const uploadingCount = React.useMemo(
    () =>
      uploads.filter(
        (u) =>
          u.status === UPLOAD_STATUSES.UPLOADING.value ||
          u.status === UPLOAD_STATUSES.QUEUED.value ||
          u.status === UPLOAD_STATUSES.MOVING_TO_PROCESSING.value
      ).length,
    [uploads]
  );
  const allCancelling = React.useMemo(
    () =>
      uploads.length > 0 &&
      uploads.every((u) => u.status === UPLOAD_STATUSES.CANCELLING.value),
    [uploads]
  );

  useWarnOnUnload(uploadingCount > 0);

  const [uploadsModalOpen, setUploadsModalOpen] = useState(false);
  const { fetchActiveUploads, fetchCompletedUploads, fetchFailedUploads } =
    useContext(UploadsContext);

  const startUpload = useCallback((file, jobId = null, jobName) => {
    const clientId = uuidv4();
    const cleanedName = sanitizeFilename(file.name);
    setUploads((u) => [
      new ActiveUpload({
        clientId,
        file,
        folderId: jobId,
        folderName: jobName,
        progress: 0,
        status: UPLOAD_STATUSES.QUEUED.value,
        fileName: cleanedName,
        cleanedName,
      }),
      ...u,
    ]);
  }, []);

  const processUpload = useCallback(
    async (upload) => {
      const { clientId, file, folderId } = upload;
      const cleanedName = upload.cleanedName || sanitizeFilename(file.name);

      try {
        setUploads((u) =>
          u.map((item) =>
            item.clientId === clientId
              ? new ActiveUpload({
                  ...item,
                  status: UPLOAD_STATUSES.UPLOADING.value,
                  progress: 0,
                })
              : item
          )
        );

        const now = new Date();
        const day = now.getDate();
        const month = now.getMonth() + 1;
        const year = now.getFullYear();
        const hour = now.getHours();
        const minute = now.getMinutes();
        const second = now.getSeconds();
        const millisecond = now.getMilliseconds();
        const timestamp = `${day}_${month}_${year}_${hour}_${minute}_${second}_${millisecond}`;
        const azureFileName = `${timestamp}-${cleanedName}`;

        const payload = {
          file_name: cleanedName,
          upload_file_name: azureFileName,
          job_id: folderId,
          uploaded_time: new Date().toISOString(),
          file_size: file.size,
        };
        const { data } = await apiClient.post("/files/upload", payload);
        const { file_id, processing_file_id, blob_url_with_sas: sasUrl } = data;

        const abortController = new AzureAbortController();
        const blobServiceClient = new BlobServiceClient(sasUrl);
        const containerClient = blobServiceClient.getContainerClient(
          UPLOAD_CONTAINER_NAME
        );
        const blockBlobClient =
          containerClient.getBlockBlobClient(azureFileName);

        setUploads((u) =>
          u.map((item) =>
            item.clientId === clientId
              ? new ActiveUpload({
                  ...item,
                  status: UPLOAD_STATUSES.UPLOADING.value,
                  progress: 0,
                  id: file_id,
                  fileListId: processing_file_id,
                  controller: abortController,
                })
              : item
          )
        );

        await blockBlobClient.uploadData(file, {
          abortSignal: abortController.signal,
          concurrency: 6,
          onProgress: (ev) => {
            const percent = Math.round((ev.loadedBytes / file.size) * 100);
            setUploads((prevUploads) =>
              prevUploads.map((item) => {
                if (item.clientId === clientId) {
                  const updatedProgress = Math.max(item.progress, percent);
                  return new ActiveUpload({
                    ...item,
                    progress: updatedProgress,
                  });
                }
                return item;
              })
            );
          },
        });

        await apiClient.patch(`/files/${file_id}/upload-complete`);

        setUploads((u) =>
          u.map((item) =>
            item.clientId === clientId
              ? new ActiveUpload({
                  ...item,
                  status: UPLOAD_STATUSES.MOVING_TO_PROCESSING.value,
                  fileListId: processing_file_id,
                })
              : item
          )
        );

        fetchActiveUploads(true);
        fetchCompletedUploads(true);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error(err);
          setUploads((u) =>
            u.map((item) =>
              item.clientId === upload.clientId
                ? {
                    ...item,
                    status: UPLOAD_STATUSES.UPLOAD_FAILED.value,
                    errorMsg: err.response?.data?.detail || err.message || "Unknown error",
                  }
                : item
            )
          );
          if (upload.fileListId) {
            apiClient
              .delete(`/files/processing/${upload.fileListId}`)
              .catch(console.error);
          }
        }
      }
    },
    [fetchActiveUploads, fetchCompletedUploads]
  );

  useEffect(() => {
    const active = uploads.filter(
      (u) => u.status === UPLOAD_STATUSES.UPLOADING.value
    ).length;
    const queued = uploads.filter(
      (u) => u.status === UPLOAD_STATUSES.QUEUED.value
    );
    const slots = MAX_CONCURRENT_UPLOADS - active;
    if (slots > 0 && queued.length > 0) {
      const uploadsToProcess = queued.slice(0, slots);
      uploadsToProcess.forEach((upload) => processUpload(upload));
    }
  }, [uploads]);

  useEffect(() => {
    if (!uploads || !activeUploads) return;

    uploads.forEach((localUpload) => {
      if (
        localUpload.status === UPLOAD_STATUSES.MOVING_TO_PROCESSING.value &&
        localUpload.fileListId
      ) {
        const serverUpload = activeUploads.find(
          (serverUpload) => serverUpload.id === localUpload.fileListId
        );

        if (serverUpload) {
          setUploads((u) =>
            u.map((item) =>
              item.fileListId === localUpload.fileListId
                ? new ActiveUpload({
                    ...item,
                    status: UPLOAD_STATUSES.COMPLETE.value,
                    completedAt: new Date(),
                  })
                : item
            )
          );
          showSuccessNotification(
            "Upload Complete",
            `${localUpload.fileName} uploaded successfully!`
          );
        }
      }
    });
  }, [uploads, activeUploads]);

  const clearCompletedUpload = useCallback((clientId) => {
    setUploads((u) => u.filter((upload) => upload.clientId !== clientId));
  }, []);

  const clearAllCompletedUploads = useCallback(() => {
    setUploads((u) =>
      u.filter((upload) => upload.status !== UPLOAD_STATUSES.COMPLETE.value)
    );
  }, []);

  const cancelAllUploads = useCallback(() => {
    setUploads((u) => {
      const uploadsToCancel = u.filter(
        (upload) =>
          upload.status === UPLOAD_STATUSES.UPLOADING.value ||
          upload.status === UPLOAD_STATUSES.QUEUED.value ||
          upload.status === UPLOAD_STATUSES.MOVING_TO_PROCESSING.value
      );

      uploadsToCancel.forEach((item) => {
        if (item.status === UPLOAD_STATUSES.UPLOADING.value) {
          item.controller?.abort();
        }
      });

      return u.map((item) => {
        if (uploadsToCancel.includes(item)) {
          return new ActiveUpload({
            ...item,
            status: UPLOAD_STATUSES.CANCELLING.value,
          });
        }
        return item;
      });
    });

    setTimeout(async () => {
      const uploadsToDelete = uploads.filter(
        (upload) => upload.status === UPLOAD_STATUSES.CANCELLING.value
      );

      const deletePromises = uploadsToDelete
        .filter((item) => item.fileListId)
        .map((item) =>
          apiClient
            .delete(`/files/processing/${item.fileListId}`)
            .catch(console.error)
        );

      await Promise.all(deletePromises);

      setUploads((u) =>
        u.filter((upload) => upload.status !== UPLOAD_STATUSES.CANCELLING.value)
      );
    }, 500);
  }, [uploads]);

  const cancelUpload = useCallback(
    (clientId) => {
      setUploads((u) => {
        const upload = u.find((item) => item.clientId === clientId);
        if (!upload) return u;

        if (upload.status === UPLOAD_STATUSES.QUEUED.value) {
          return u.filter((item) => item.clientId !== clientId);
        }

        if (upload.status === UPLOAD_STATUSES.UPLOADING.value) {
          upload.controller?.abort();
        }

        return u.map((item) =>
          item.clientId === clientId
            ? { ...item, status: UPLOAD_STATUSES.CANCELLING.value }
            : item
        );
      });

      setTimeout(async () => {
        const upload = uploads.find((item) => item.clientId === clientId);

        if (upload?.fileListId) {
          try {
            await apiClient.delete(`/files/processing/${upload.fileListId}`);
          } catch (error) {
            console.error("Error deleting processing file:", error);
          }
        }

        setUploads((u) => u.filter((item) => item.clientId !== clientId));
      }, 500);
    },
    [uploads]
  );

  const retryUpload = useCallback(
    async (clientId) => {
      const existing = uploads.find((u) => u.clientId === clientId);
      if (existing?.fileListId) {
        try {
          await apiClient.delete(`/files/processing/${existing.fileListId}`);
        } catch {
          // Best effort: the upload is re-queued whether or not the old record was removed.
        }
      }

      setUploads((all) =>
        all.map((u) =>
          u.clientId === clientId
            ? new ActiveUpload({
                ...u,
                status: UPLOAD_STATUSES.QUEUED.value,
                progress: 0,
                errorMsg: null,
                controller: null,
              })
            : u
        )
      );
    },
    [uploads]
  );

  useEffect(() => {
    if (uploadingCount > 0) {
      if (!isDocumentVisible) {
        return;
      }

      fetchActiveUploads(true);
      fetchCompletedUploads(true);
      fetchFailedUploads(true);

      const intervalId = setInterval(() => {
        fetchActiveUploads(true);
        fetchCompletedUploads(true);
        fetchFailedUploads(true);
      }, 10000);

      return () => clearInterval(intervalId);
    }
  }, [
    uploadingCount,
    fetchActiveUploads,
    fetchCompletedUploads,
    fetchFailedUploads,
    isDocumentVisible,
  ]);

  return (
    <UploadContext.Provider
      value={{
        uploads,
        startUpload,
        cancelUpload,
        retryUpload,
        setUploadsModalOpen,
        uploadingCount,
        clearCompletedUpload,
        clearAllCompletedUploads,
        cancelAllUploads,
        allCancelling,
      }}
    >
      {children}
      <UploadsModal
        opened={uploadsModalOpen}
        onClose={() => setUploadsModalOpen(false)}
      />
    </UploadContext.Provider>
  );
}
